<template>
  <div class="container mt-4">
    <div class="card p-4 shadow border-0" style="border-radius: 16px;">
      <h4 class="mb-4" style="color: #b03a45;">
        <i class="fas fa-briefcase me-2"></i> {{ $t('position.title') }}
      </h4>

      <!-- Form -->
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('position.code') }}</label>
              <input type="text" class="form-control" v-model="form.code" required />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('position.englishName') }}</label>
              <input type="text" class="form-control" v-model="form.english_name" required />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('position.arabicName') }}</label>
              <input type="text" class="form-control" v-model="form.arabic_name" />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded d-flex align-items-center justify-content-between">
              <label class="form-label mb-0">{{ $t('position.status') }}</label>
              <div class="form-check form-switch">
                <input type="checkbox" class="form-check-input" v-model="form.is_active" />
                <label class="form-check-label">{{ form.is_active ? $t('position.active') : $t('position.inactive') }}</label>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex gap-3 mt-3">
          <button type="submit" class="btn text-white px-4" style="background-color: #b03a45;">
            {{ editingId ? $t('position.update') : $t('position.add') }}
          </button>
          <button type="button" class="btn btn-secondary px-4" @click="resetForm">{{ $t('position.cancel') }}</button>
        </div>
      </form>

      <!-- List -->
      <hr class="my-4" />
      <h5 class="mb-3" style="color: #b03a45;">
        <i class="fas fa-list me-2"></i> {{ $t('position.existingPositions') }}
      </h5>

      <div v-if="positions.length" class="table-responsive">
        <table class="table table-bordered align-middle">
          <thead class="table-light">
            <tr>
              <th>{{ $t('position.code') }}</th>
              <th>{{ $t('position.englishName') }}</th>
              <th>{{ $t('position.arabicName') }}</th>
              <th>{{ $t('position.status') }}</th>
              <th class="text-center">{{ $t('position.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="position in positions" :key="position.id">
              <td>{{ position.code }}</td>
              <td>{{ position.english_name }}</td>
              <td>{{ position.arabic_name || '-' }}</td>
              <td>
                <span :class="position.is_active ? 'text-success' : 'text-danger'">
                  {{ position.is_active ? $t('position.active') : $t('position.inactive') }}
                </span>
              </td>
              <td class="text-center">
                <button class="btn btn-outline-danger btn-sm me-2" @click="loadPosition(position)">{{ $t('position.edit') }}</button>
                <button class="btn btn-outline-secondary btn-sm" @click="deletePosition(position.id)">{{ $t('position.delete') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="text-muted">{{ $t('position.noPositionsFound') }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import positionService from '@/services/positionService'
import { useToast } from 'vue-toastification'

const toast = useToast()
const positions = ref([])
const editingId = ref(null)

const form = ref({
  code: '',
  english_name: '',
  arabic_name: '',
  is_active: true
})

const fetchPositions = async () => {
  try {
    const response = await positionService.getAll()
    positions.value = response.data
  } catch (err) {
    toast.error($t('position.failedToLoadPositions'))
    console.error(err)
  }
}

const handleSubmit = async () => {
  try {
    if (editingId.value) {
      await positionService.update(editingId.value, form.value)
      toast.success($t('position.positionUpdated'))
    } else {
      await positionService.create(form.value)
      toast.success($t('position.positionAdded'))
    }
    resetForm()
    fetchPositions()
  } catch (err) {
    toast.error($t('position.failedToSavePosition'))
    console.error(err)
  }
}

const loadPosition = (position) => {
  editingId.value = position.id
  form.value = {
    code: position.code,
    english_name: position.english_name,
    arabic_name: position.arabic_name,
    is_active: position.is_active
  }
}

const deletePosition = async (id) => {
  if (!confirm($t('position.confirmDelete'))) return
  try {
    await positionService.delete(id)
    toast.success($t('position.positionDeleted'))
    fetchPositions()
  } catch (err) {
    toast.error($t('position.failedToDeletePosition'))
    console.error(err)
  }
}

const resetForm = () => {
  editingId.value = null
  form.value = {
    code: '',
    english_name: '',
    arabic_name: '',
    is_active: true
  }
}

onMounted(() => {
  fetchPositions()
})
</script>
